import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDuration } from '@vendorops/shared';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Field, durationText } from '@/components/TimelineItem';
import { DRAFT_DURATION_MINUTES } from '@/components/TimelineAxis';
import HoursMinutesField, {
  DURATION_TOO_LONG_HINT,
  OFFSET_TOO_FAR_HINT,
  durationFields,
  durationOutOfRange,
  fieldsFromMinutes,
  minutesFromFields,
  offsetOutOfRange,
  type HoursMinutes,
} from '@/components/HoursMinutesField';

/**
 * The planner's default day-of ladder, edited on the Templates page (PRD §F2).
 *
 * The one structural difference from the wedding's timeline panel drives
 * everything here: a template has no wedding, so it has no start time, so it has
 * no clock times. An offset is the only spelling available. That is why this is
 * not `TimelinePanel` with a different header and why `TimelineItemForm` is not
 * reused — its central input is a `TimePicker`, and there is nothing here for a
 * clock time to be relative to. What *is* shared is the vocabulary: `Field` and
 * `durationText` come from `TimelineItem`, and `HoursMinutesField` — this card's
 * own control, now the length field in both forms — is a component both import.
 * A length reads the same word and is typed the same way in both places.
 *
 * Offsets are never shown as the integers they are. `-210` is "3 hr 30 min
 * before start", and it is typed as `3` hr `30` min `before start` — three plain
 * controls, no minus sign, no arithmetic.
 *
 * The save is one `PUT` of the whole array, on an explicit press. Editing a row
 * changes a local draft and nothing else: this ladder is what every new wedding
 * is drafted from, and a half-finished one must never become that. Which also
 * means an emptied template is answered here rather than at the server — the
 * planner who just deleted their last row is owed the sentence immediately.
 */

/** Said before a round trip; the server's `min(1)` says it again behind us. */
const EMPTY_TEMPLATE_MESSAGE =
  'A template needs at least one item — every new wedding is drafted from it. Add a row before saving.';

/** `timelineTemplateSchema`'s ceiling, said here so a save cannot fail vaguely. */
const MAX_TEMPLATE_ITEMS = 60;
const FULL_TEMPLATE_MESSAGE = `A template holds at most ${MAX_TEMPLATE_ITEMS} items. Remove one before saving.`;

/** A row being edited, with a local identity the stored items do not have. */
interface DraftItem {
  /**
   * Local only, and deliberately not sent. The stored template is an ordered
   * list with no identity of its own — see the route's "no PATCH twin" note —
   * so this exists to key a React list and to name which row is open, nothing
   * more.
   */
  key: string;
  label: string;
  offsetMinutes: number;
  durationMinutes: number | null;
  note: string | null;
}

/** The stored shape, as it arrives from `GET /api/timeline-template`. */
interface StoredItem {
  label: string;
  offsetMinutes: number;
  durationMinutes?: number | null;
  note?: string | null;
}

/**
 * An offset, as a relation to the start of the wedding.
 *
 * Two parts rather than one string so the row can set the magnitude apart from
 * the relation — twelve rows of "before start" / "after start" read as noise at
 * full strength — while `relationToStart` below keeps a single source for the
 * words themselves. `formatDuration` supplies the magnitude so the wording
 * matches every other length in the feature.
 */
function relationParts(offsetMinutes: number): { magnitude: string; relation: string | null } {
  // Not "0 min after start", which is what the magnitude would say on its own.
  if (offsetMinutes === 0) return { magnitude: 'At start', relation: null };
  return {
    magnitude: formatDuration(Math.abs(offsetMinutes)),
    relation: offsetMinutes < 0 ? 'before start' : 'after start',
  };
}

/** The same relation as one sentence, for a form that reads a number back. */
function relationToStart(offsetMinutes: number): string {
  const { magnitude, relation } = relationParts(offsetMinutes);
  return relation ? `${magnitude} ${relation}` : magnitude;
}

function toDraft(items: readonly StoredItem[]): DraftItem[] {
  return items.map((item, i) => ({
    // Index-based and assigned once on load: the rows are reordered for display
    // by offset, never by this.
    key: `stored-${i}`,
    label: item.label,
    offsetMinutes: item.offsetMinutes,
    durationMinutes: item.durationMinutes ?? null,
    note: item.note ?? null,
  }));
}

/** Which form, if any, is open. One at a time — see the `aria-label` reuse. */
type OpenForm = { kind: 'add' } | { kind: 'row'; key: string } | null;

export default function TimelineTemplateCard() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<DraftItem[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [open, setOpen] = useState<OpenForm>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    // Its own key, deliberately not folded into `['user-settings']`: that route
    // is a per-field partial update and this one replaces the whole template, so
    // sharing a key would have a signature save and a template save invalidating
    // each other with a stale ladder in flight.
    queryKey: ['timeline-template'],
    queryFn: async () => {
      const res = await client.api['timeline-template'].$get();
      if (!res.ok) throw new Error('Failed to load your timeline template');
      return res.json();
    },
  });

  // The same shape the identity section above uses: the server's answer seeds
  // the draft until the planner has touched it, and then stops, so a background
  // refetch cannot overwrite edits in progress.
  useEffect(() => {
    if (data && !dirty) setDraft(toDraft(data.items));
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: async (rows: DraftItem[]) => {
      const res = await client.api['timeline-template'].$put({
        // The local `key` is dropped here rather than being carried along: the
        // server stores exactly what it is sent.
        json: {
          items: rows.map(({ label, offsetMinutes, durationMinutes, note }) => ({
            label,
            offsetMinutes,
            durationMinutes,
            note,
          })),
        },
      });
      const body = await res.json();
      // `ok` has already excluded the 404 "planner not found" branch; the union
      // the client infers still carries it, so the narrowing is explicit.
      if (!res.ok || !('items' in body)) {
        const stated = 'error' in body ? body.error : undefined;
        throw new Error(typeof stated === 'string' ? stated : 'Could not save your template');
      }
      return body;
    },
    onSuccess: () => {
      setDirty(false);
      setOpen(null);
      setFailure(null);
      queryClient.invalidateQueries({ queryKey: ['timeline-template'] });
      toast.success('Template saved');
    },
    onError: (err: Error) => setFailure(err.message),
  });

  /** Ordered by offset, which is the only order a day has. */
  const items = [...(draft ?? [])].sort((a, b) => a.offsetMinutes - b.offsetMinutes);
  const emptied = draft !== null && draft.length === 0;
  const overfull = items.length > MAX_TEMPLATE_ITEMS;
  // One line, whichever went wrong. What the draft itself says outranks a stale
  // failure from a previous attempt, because it is the thing to fix now.
  const message = emptied
    ? EMPTY_TEMPLATE_MESSAGE
    : overfull
      ? FULL_TEMPLATE_MESSAGE
      : failure;

  /**
   * Every edit lands here and nowhere else.
   *
   * No undo offer on a removal, unlike the wedding's panel: nothing has left the
   * planner's screen yet. Reset puts the whole stored ladder back, and a row that
   * is never saved was never lost.
   */
  const change = (next: DraftItem[]) => {
    setDraft(next);
    setDirty(true);
    setFailure(null);
  };

  return (
    <Card className="rounded-3xl bg-card shadow-ambient">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="label-caps text-xs text-muted-foreground">Timeline template</h2>
          {open?.kind !== 'add' && draft && (
            <Button variant="outline" size="sm" onClick={() => setOpen({ kind: 'add' })}>
              <Plus className="size-3.5" />
              Add an item
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Every new wedding’s timeline is drafted from this ladder, then adjusted for that day.
          Changing it here leaves the weddings you have already drafted exactly as they are.
        </p>

        {isLoading && <p className="text-sm text-muted-foreground">Loading your template…</p>}
        {isError && (
          <p className="text-sm text-muted-foreground">
            Your template didn’t load. Reload the page to try again.
          </p>
        )}

        {/* Provenance, said plainly. Twelve rows presented without this read as
            twelve rows the planner wrote and then forgot writing. */}
        {data?.isDefault && (
          <p className="flex items-start gap-2 rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-3 text-sm text-foreground/75">
            <Info className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
            <span>
              This is the built-in ladder — you haven’t saved one of your own yet. Change anything
              and save, and these rows become yours to keep.
            </span>
          </p>
        )}

        {message && (
          <p role="alert" className="text-sm text-destructive">
            {message}
          </p>
        )}

        {items.length > 0 && (
          <ol className="divide-y divide-border">
            {items.map((item) =>
              open?.kind === 'row' && open.key === item.key ? (
                <li key={item.key} className="py-3">
                  <TemplateItemForm
                    idPrefix={`template-${item.key}`}
                    initial={item}
                    submitLabel="Done"
                    onCancel={() => setOpen(null)}
                    onSubmit={(edited) => {
                      change(
                        items.map((row) =>
                          row.key === item.key ? { ...row, ...edited } : row,
                        ),
                      );
                      setOpen(null);
                    }}
                  />
                </li>
              ) : (
                <TemplateRow
                  key={item.key}
                  item={item}
                  onEdit={() => setOpen({ kind: 'row', key: item.key })}
                  onRemove={() => change(items.filter((row) => row.key !== item.key))}
                />
              ),
            )}
          </ol>
        )}

        {open?.kind === 'add' && (
          <TemplateItemForm
            idPrefix="template-new"
            /* See `DRAFT_DURATION_MINUTES`. */
            initial={{ label: '', offsetMinutes: 0, durationMinutes: DRAFT_DURATION_MINUTES, note: null }}
            submitLabel="Add"
            onCancel={() => setOpen(null)}
            onSubmit={(added) => {
              change([...items, { key: `new-${Date.now()}-${items.length}`, ...added }]);
              setOpen(null);
            }}
          />
        )}

        {draft && (
          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <Button
              variant="outline"
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => {
                if (data) setDraft(toDraft(data.items));
                setDirty(false);
                setOpen(null);
                setFailure(null);
              }}
            >
              Reset
            </Button>
            {/* Enabled while the template is empty rather than disabled: a
                planner who deleted their last row gets the sentence saying why,
                not a control that has quietly stopped responding. */}
            <Button
              size="sm"
              disabled={!dirty || save.isPending}
              onClick={() => {
                // Refused here rather than round-tripped. The message is already
                // on screen either way; what this stops is a request that cannot
                // succeed and a planner waiting on it.
                if (emptied || overfull) return;
                save.mutate(items);
              }}
            >
              {save.isPending ? 'Saving…' : 'Save template'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** One row of the ladder: where it sits, what it is, how long it runs. */
function TemplateRow({
  item,
  onEdit,
  onRemove,
}: {
  item: DraftItem;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { magnitude, relation } = relationParts(item.offsetMinutes);

  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-0.5 py-3 sm:flex-nowrap sm:gap-4">
      {/* One unbroken line, and wide enough for the longest relation a planner
          can enter — "47 hr 59 min before start". A column narrow enough to wrap
          breaks it mid-phrase, and "45 min before / start" is not a phrase.
          Tabular figures because this is the column the eye runs down, and
          proportional digits make a ladder wobble. On a phone it takes the whole
          first line instead of squeezing the label off the row. */}
      <p className="w-full shrink-0 text-sm whitespace-nowrap sm:w-[11rem]">
        <span className="font-medium tabular-nums">{magnitude}</span>
        {relation && <span className="text-muted-foreground">{` ${relation}`}</span>}
      </p>

      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="text-sm">{item.label}</p>
        <p className="text-xs text-muted-foreground">
          {durationText(item.durationMinutes)}
          {item.note && ` · ${item.note}`}
        </p>
      </div>

      {/* Always visible, never on hover: a hover-only control is unreachable on
          the phone a planner opens Settings on. Muted until wanted instead. */}
      <div className="flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          aria-label={`Edit ${item.label}`}
          onClick={onEdit}
          className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-[var(--color-surface-container-high)] hover:text-foreground focus-visible:outline-1 focus-visible:outline-primary/50"
        >
          <Pencil className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label={`Remove ${item.label}`}
          onClick={onRemove}
          className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-[var(--color-surface-container-high)] hover:text-destructive focus-visible:outline-1 focus-visible:outline-primary/50"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </li>
  );
}

/** What a planner fills in for a template row. No clock time: there is no clock. */
type TemplateItemDraft = Omit<DraftItem, 'key'>;

/**
 * The one form for adding a template row and for correcting one.
 *
 * The time is said the way a planner says it — "an hour and a half before" — as
 * hours, minutes and a direction, each a plain positive control. Two fields
 * rather than one minute count because the alternative is asking a planner to
 * multiply: "three and a half hours before" is `3` and `30`, not `210`. And a
 * direction rather than a sign, because nobody should ever have to type a minus
 * sign to say "before".
 *
 * `relationToStart` reads the three controls back as one sentence while they are
 * typed, through the same words the rows use, so the form and the row it becomes
 * cannot disagree.
 *
 * The length is the same control again, which is the point: a form that asked for
 * one span of time in hours and minutes and the other as a raw minute count is a
 * form that invites the right number into the wrong field. The two pairs differ
 * in exactly one place, and it is the one place they mean something different —
 * an empty pair is zero for an offset and open-ended for a length.
 */
function TemplateItemForm({
  idPrefix,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  idPrefix: string;
  initial: TemplateItemDraft;
  submitLabel: string;
  onSubmit: (draft: TemplateItemDraft) => void;
  onCancel: () => void;
}) {
  const [offset, setOffset] = useState<HoursMinutes>(fieldsFromMinutes(initial.offsetMinutes));
  const [direction, setDirection] = useState(initial.offsetMinutes > 0 ? 'after' : 'before');
  const [label, setLabel] = useState(initial.label);
  const [duration, setDuration] = useState<HoursMinutes>(durationFields(initial.durationMinutes));
  const [note, setNote] = useState(initial.note ?? '');

  // An empty pair is zero here, not nothing: an item at the start of the wedding
  // is an ordinary item, and "no offset at all" is not an answer an offset has.
  // A duration's empty pair *is* nothing, which is why the two read differently.
  const magnitude = minutesFromFields(offset) ?? 0;
  const offsetInvalid = offsetOutOfRange(magnitude);
  // Zero is neither before nor after, and `relationParts` says so regardless of
  // which way the direction happens to be pointing.
  const offsetMinutes = direction === 'before' ? -magnitude : magnitude;
  const offsetHint = offsetInvalid
    ? OFFSET_TOO_FAR_HINT
    : relationToStart(offsetMinutes);

  const parsedDuration = minutesFromFields(duration);
  const durationInvalid = durationOutOfRange(parsedDuration);
  const durationHint = durationInvalid
    ? DURATION_TOO_LONG_HINT
    : durationText(parsedDuration);

  const ready = label.trim() !== '' && !offsetInvalid && !durationInvalid;

  return (
    <form
      className="space-y-3 rounded-2xl bg-[var(--color-surface-container-low)] p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!ready) return;
        onSubmit({
          label: label.trim(),
          offsetMinutes,
          durationMinutes: parsedDuration,
          note: note.trim() === '' ? null : note.trim(),
        });
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <HoursMinutesField
        idPrefix={`${idPrefix}-offset`}
        legend="When it happens"
        hoursLabel="Hours from the start"
        minutesLabel="Minutes from the start"
        value={offset}
        onChange={setOffset}
        invalid={offsetInvalid}
        hint={offsetHint}
        trailing={
          <NativeSelect
            id={`${idPrefix}-direction`}
            aria-label="Before or after the start"
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
          >
            <option value="before">before start</option>
            <option value="after">after start</option>
          </NativeSelect>
        }
      />

      <Field id={`${idPrefix}-label`} label="What happens">
        <Input
          id={`${idPrefix}-label`}
          value={label}
          maxLength={120}
          placeholder="Ceremony"
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>

      <div className="flex flex-wrap items-start gap-3">
        <HoursMinutesField
          idPrefix={`${idPrefix}-duration`}
          legend="How long it runs"
          hoursLabel="Length in hours"
          minutesLabel="Length in minutes"
          value={duration}
          onChange={setDuration}
          invalid={durationInvalid}
          hint={durationHint}
        />

        <Field className="min-w-48 flex-1" id={`${idPrefix}-note`} label="Note">
          <Input
            id={`${idPrefix}-note`}
            value={note}
            maxLength={500}
            placeholder="Anything every wedding needs reminding of"
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={!ready}>
          {submitLabel}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
